import { useI18n } from 'vue-i18n'
import { type Locale, availableLocales, defaultLocale } from '@/locales'
import { isClient } from '@/composables/useSSR'

/**
 * Composable for managing application locale.
 * Syncs vue-i18n locale with user preferences and browser settings.
 */
export function useLocale() {
  const { locale } = useI18n()

  /**
   * Set the application locale.
   * @param lang - The locale code ('de' or 'en')
   */
  function setLocale(lang: Locale | string) {
    if (isValidLocale(lang)) {
      // The <html lang> attribute is kept in sync by the immediate locale watcher
      // in App.vue (single source of truth), so no DOM write is needed here.
      locale.value = lang
    }
  }

  /**
   * Check if a locale code is valid/supported.
   */
  function isValidLocale(lang: string): lang is Locale {
    return availableLocales.includes(lang as Locale)
  }

  /**
   * Get the current locale.
   */
  function getLocale(): Locale {
    return locale.value as Locale
  }

  /**
   * Initialize locale from account data.
   * Falls back to browser language or default if no account data.
   * @param accountLang - The language from account details (optional)
   */
  function initLocaleFromAccount(accountLang?: string | null) {
    if (accountLang && isValidLocale(accountLang)) {
      setLocale(accountLang)
      return
    }

    // Fallback to browser language detection
    const browserLang = detectBrowserLocale()
    if (browserLang) {
      setLocale(browserLang)
      return
    }

    // Final fallback to default
    setLocale(defaultLocale)
  }

  /**
   * Detect browser's preferred locale from available locales.
   * @returns The matched locale or null
   */
  function detectBrowserLocale(): Locale | null {
    if (!isClient) return null

    // Check navigator.languages first (ordered by preference)
    const languages = navigator.languages || [navigator.language]

    for (const lang of languages) {
      // Try exact match first (e.g., 'en-GB')
      const normalised = lang.toLowerCase()

      // Check if it starts with any of our supported locales
      for (const supported of availableLocales) {
        if (normalised === supported || normalised.startsWith(`${supported}-`)) {
          return supported
        }
      }
    }

    return null
  }

  return {
    locale,
    setLocale,
    getLocale,
    initLocaleFromAccount,
    detectBrowserLocale,
    isValidLocale
  }
}
